import { faWrench } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import ConvertedUnits from 'src/components/ConvertedUnits';
import Loader from 'src/components/loader';
import { INVENTORY_ACTION_TYPES } from 'src/constants/inventory';

/**
 * Displays a real-time summary of inventory quantities
 * Shows different information based on whether it's a group or individual piece assignment
 * Compacts to show only total when scrolled down
 */
const InventoryQuantitySummary = ({
  action,
  currentSum,
  units,
  groupQuantity,
  pieceName,
  pieceGroupIndex,
  isLoadingContainers,
}) => {
  const [isCompact, setIsCompact] = useState(false);
  const summaryRef = useRef(null);
  const isGroupLevel = action === INVENTORY_ACTION_TYPES.GROUP;
  const totalQuantity = isGroupLevel ? currentSum : currentSum + groupQuantity;

  // Detect scroll to compact/expand the summary
  useEffect(() => {
    if (!summaryRef.current) return;

    // Find all scrollable parent elements
    const getScrollableParents = (element) => {
      const parents = [];
      let parent = element.parentElement;

      while (parent) {
        const { overflowY } = window.getComputedStyle(parent);
        if (overflowY === 'auto' || overflowY === 'scroll') {
          parents.push(parent);
        }
        parent = parent.parentElement;
      }

      return parents;
    };

    const handleScroll = () => {
      const scrollParents = getScrollableParents(summaryRef.current);
      const scrollPosition = scrollParents.length > 0
        ? scrollParents[0].scrollTop
        : (window.scrollY || document.documentElement.scrollTop);

      // Set compact mode if scrolled more than 120px
      setIsCompact(scrollPosition > 120);
    };

    // Initial check
    handleScroll();

    // Attach scroll listeners
    const scrollParents = getScrollableParents(summaryRef.current);
    window.addEventListener('scroll', handleScroll, { passive: true });
    scrollParents.forEach(parent => {
      parent.addEventListener('scroll', handleScroll, { passive: true });
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      scrollParents.forEach(parent => {
        parent.removeEventListener('scroll', handleScroll);
      });
    };
  }, []);


  const renderQuantity = (value = totalQuantity) =>
    isLoadingContainers ? (
      <Loader inline showText={false} />
    ) : (
      <ConvertedUnits displayZero quantity={value} units={units} />
    );

  if (isGroupLevel) {
    // Instruction Group Level - Single Row
    return (
      <div ref={summaryRef} className="inv-summary inv-summary-group">
        <div className="inv-summary-content">
          <div className="inv-summary-item-label font-weight-700">Total</div>
          <div className="inv-summary-total-group">
            {renderQuantity()}
          </div>
        </div>
      </div>
    );
  }

  // Piece Level - Show compact or full view based on scroll
  if (isCompact) {
    return (
      <div ref={summaryRef} className="inv-summary inv-summary-compact">
        <div className="inv-summary-content">
          <div className="inv-summary-item-label font-weight-700">Total</div>
          <div className="inv-summary-total">
            {renderQuantity()}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={summaryRef} className="inv-summary inv-summary-expanded">
      <div className="inv-summary-grid">
        <div className="inv-summary-item">
          <div className="inv-summary-item-label">
            From <span className="inv-summary-group-number">Group {pieceGroupIndex}</span>
          </div>
          <div className="inv-summary-item-value">
            <ConvertedUnits displayZero quantity={groupQuantity} units={units} />
          </div>
        </div>
        <div className="inv-summary-divider" />
        <div className="inv-summary-item inv-summary-item-current">
          <div className="inv-summary-item-label">
            <FontAwesomeIcon icon={faWrench} className="inv-summary-piece-icon" />
            <span className="inv-summary-piece-name">{pieceName || '[Not Rendered]'}</span>
          </div>
          <div className="inv-summary-item-value inv-summary-item-value-bold">
            {renderQuantity(currentSum)}
          </div>
        </div>
        <div className="inv-summary-divider" />
        <div className="inv-summary-item inv-summary-item-total">
          <div className="inv-summary-item-label font-weight-700">Total</div>
          <div className="inv-summary-total">
            {renderQuantity()}
          </div>
        </div>
      </div>
    </div>
  );
};

InventoryQuantitySummary.propTypes = {
  action: PropTypes.oneOf([INVENTORY_ACTION_TYPES.GROUP, INVENTORY_ACTION_TYPES.PIECE]).isRequired,
  currentSum: PropTypes.number.isRequired,
  units: PropTypes.string.isRequired,
  groupQuantity: PropTypes.number,
  pieceName: PropTypes.string,
  pieceGroupIndex: PropTypes.number,
  isLoadingContainers: PropTypes.bool,
};

InventoryQuantitySummary.defaultProps = {
  groupQuantity: 0,
  pieceName: null,
  pieceGroupIndex: null,
  isLoadingContainers: false,
};

export default InventoryQuantitySummary;

